import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, CheckCircle2, TrendingUp, Home, Sparkles, Building2, ArrowRight, ArrowLeft } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { trackFormEvent } from "@/lib/formTracking";
import EnrichmentInfoPanel from "./EnrichmentInfoPanel";
import useAddressAutocomplete from "@/hooks/useAddressAutocomplete";

const FORM_NAME = "Valuation Request";
const TOTAL_STEPS = 5;

const GOALS = [
  { id: "hassle", icon: Sparkles, label: "Remove the hassle of hosting myself" },
  { id: "switch", icon: Home, label: "Turn one of my long-lets into a short-let" },
  { id: "estimate", icon: TrendingUp, label: "Get a short-let income estimate" },
  { id: "multiple", icon: Building2, label: "Manage multiple properties on short-lets" },
];

const CONDITIONS = [
  { name: "Basic", img: "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/a3007c306_Basic.png" },
  { name: "Average", img: "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/7709491e6_Average.png" },
  { name: "Good", img: "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/c88efb4e4_Good.png" },
  { name: "Luxury", img: "https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/ef64c3348_Luxury.png" },
];

const PARKING = ["Street parking", "Private parking", "Paid parking", "No parking"];

const BED_LABELS = ["Studio", "1 bed", "2 beds", "3 beds", "4 beds", "5 beds", "6 beds", "7+ beds"];
const bedToNum = (s) => BED_LABELS.indexOf(s) >= 0 ? BED_LABELS.indexOf(s) : 2;
const numToBed = (n) => BED_LABELS[n] || "2 beds";

const BED_OPTS = [
  { label: "Studio", value: 0 },
  { label: "1", value: 1 },
  { label: "2", value: 2 },
  { label: "3", value: 3 },
  { label: "4", value: 4 },
  { label: "5", value: 5 },
  { label: "6", value: 6 },
  { label: "7+", value: 7 },
];

function tapBtnStyle(sel) {
  return {
    padding: "12px 10px",
    border: sel ? "2px solid #112215" : "1.5px solid #E8E4D5",
    borderRadius: 10,
    background: sel ? "#F4F1E7" : "#fff",
    fontFamily: "'Inter', sans-serif",
    fontSize: 14,
    fontWeight: 500,
    color: "#112215",
    cursor: "pointer",
    transition: "border-color 0.15s, background 0.15s",
    boxSizing: "border-box",
  };
}

function TapOptions({ value, onPick, options, columns }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 8 }}>
      {options.map((opt) => {
        const sel = value === opt.value;
        return (
          <button
            key={String(opt.value)}
            type="button"
            onClick={() => onPick(opt.value)}
            style={tapBtnStyle(sel)}
            onMouseEnter={(e) => { if (!sel) e.currentTarget.style.borderColor = "#B8B4A5"; }}
            onMouseLeave={(e) => { if (!sel) e.currentTarget.style.borderColor = "#E8E4D5"; }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

const inputStyle = {
  border: "1.5px solid #E8E4D5", borderRadius: 10, padding: "13px 16px",
  fontFamily: "'Inter', sans-serif", fontSize: 15, color: "#112215",
  background: "#fff", width: "100%", boxSizing: "border-box", outline: "none",
  transition: "border-color 0.2s",
};
const labelStyle = {
  fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600,
  letterSpacing: "0.07em", textTransform: "uppercase", color: "#5C7268",
  display: "block", marginBottom: 6, textAlign: "left",
};
const errStyle = { color: "#CD533B", fontSize: 11, marginTop: 4, fontFamily: "'Inter', sans-serif" };

export default function EnrichmentModal({ baseForm = {}, mode = "enrichment", onClose, onSubmitted, offerCode = "" }) {
  const isFull = mode === "full";

  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    fullName: baseForm.fullName || "", email: baseForm.email || "", phone: baseForm.phone || "",
    postcode: baseForm.postcode || "", bedrooms: baseForm.bedrooms || numToBed(bedToNum(baseForm.bedrooms)), propertyType: baseForm.propertyType || "",
  });
  const [bedNum, setBedNum] = useState(bedToNum(baseForm.bedrooms));
  const [extra, setExtra] = useState({
    address: baseForm.address || baseForm.postcode || "", goal: "", condition: "",
    parking: "", hasOtherProperties: "", listingLink: "",
  });
  const [referralConsent, setReferralConsent] = useState(true);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const addressRef = useRef(null);
  const hasSubmittedRef = useRef(false);
  const advanceTimer = useRef(null);
  const stepRef = useRef(0);
  const formRef = useRef(form);
  const extraRef = useRef(extra);
  useEffect(() => { stepRef.current = step; }, [step]);
  useEffect(() => { formRef.current = form; }, [form]);
  useEffect(() => { extraRef.current = extra; }, [extra]);

  const setF = (f) => (e) => { setForm(p => ({ ...p, [f]: e.target.value })); if (errors[f]) setErrors(prev => ({ ...prev, [f]: undefined })); };
  const setE = (f) => (e) => { setExtra(p => ({ ...p, [f]: e.target.value })); if (errors[f]) setErrors(prev => ({ ...prev, [f]: undefined })); };

  const validatePhone = (phone) => {
    const d = phone.replace(/\s/g, '').replace(/-/g, '');
    return /^((\+44|0044|0)[1-9][0-9]{7,9})$/.test(d) || (d.length >= 10 && d.length <= 15);
  };

  const validateStep = (s) => {
    const e = {};
    if (!isFull) return true;
    if (s === 0) { if (!extra.goal) e.goal = "Please choose one"; }
    else if (s === 1) { if (!extra.condition) e.condition = "Please choose one"; }
    else if (s === 2) {
      if (!extra.address.trim()) e.address = "Address is required";
      if (!form.propertyType) e.propertyType = "Required";
      if (!form.bedrooms) e.bedrooms = "Required";
    } else if (s === 3) {
      if (!extra.parking) e.parking = "Required";
      if (!extra.hasOtherProperties) e.hasOtherProperties = "Required";
    } else if (s === 4) {
      if (!form.fullName.trim()) e.fullName = "Name is required";
      if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Valid email is required";
      if (!form.phone.trim() || !validatePhone(form.phone)) e.phone = "Valid UK phone number required";
      if (!referralConsent) e.consent = "Please confirm to continue";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  useAddressAutocomplete(addressRef, (addr) => {
    setExtra(p => ({ ...p, address: addr }));
    if (errors.address) setErrors(prev => ({ ...prev, address: undefined }));
  }, { deps: [step] });

  useEffect(() => {
    if (typeof window.loadGoogleMaps === "function") window.loadGoogleMaps();
  }, []);

  useEffect(() => {
    return () => {
      clearTimeout(advanceTimer.current);
      if (!isFull && !hasSubmittedRef.current) sendEmail(false);
    };
  }, []);

  const buildEmailBody = () => {
    const f = formRef.current, e = extraRef.current;
    return [
      `=== CONTACT INFO ===`, `Name: ${f.fullName}${offerCode ? " " + offerCode : ""}`, `Email: ${f.email}`, `Phone: ${f.phone}`,
      `Postcode: ${f.postcode}`, `Bedrooms: ${f.bedrooms}`, `Property type: ${f.propertyType}`, ``,
      `=== PROPERTY DETAILS ===`, `Goal: ${e.goal || "Not provided"}`, `Full address: ${e.address || "Not provided"}`,
      `Condition: ${e.condition || "Not provided"}`, `Parking: ${e.parking || "Not provided"}`,
      `Other properties: ${e.hasOtherProperties || "Not provided"}`, `Listing link: ${e.listingLink || "Not provided"}`,
      `Referral consent: ${referralConsent ? "True" : "False"}`,
    ].join("\n");
  };

  const sendEmail = async (isComplete) => {
    if (hasSubmittedRef.current) return;
    hasSubmittedRef.current = true;
    const f = formRef.current, e = extraRef.current;
    if (f.email) {
      try {
        await base44.functions.invoke("sendLeadEmail", { subject: `New estimate – ${f.fullName}${offerCode ? " " + offerCode : ""}${isComplete ? "" : " (partial)"}`, body: buildEmailBody() });
      } catch (_) {}
      try {
        await base44.functions.invoke("saveLeadToSheet", { formData: { ...f, ...e, referralConsent, fullName: f.fullName + (offerCode ? " " + offerCode : ""), mode: isComplete ? (isFull ? "full" : "enrichment") : "enrichment" }, uploadedFiles: [] });
      } catch (_) {}
    }
    if (f.email) {
      try {
        await base44.functions.invoke("sendConfirmationEmail", {
          to: f.email, name: f.fullName, enquiryType: "valuation",
          fields: [
            { label: "Name", value: f.fullName }, { label: "Email", value: f.email },
            { label: "Phone", value: f.phone }, { label: "Property address", value: e.address },
            { label: "Property type", value: f.propertyType }, { label: "Bedrooms", value: f.bedrooms },
            { label: "Condition", value: e.condition }, { label: "Parking", value: e.parking },
            { label: "Other properties", value: e.hasOtherProperties }, { label: "Listing link", value: e.listingLink },
          ],
        });
      } catch (_) {}
    }
  };

  // Click an option card → set value, show selection briefly, auto-advance
  const selectAndAdvance = (field, value, nextStep) => {
    setExtra(p => ({ ...p, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
    clearTimeout(advanceTimer.current);
    advanceTimer.current = setTimeout(() => setStep(nextStep), 280);
  };

  const next = () => { if (validateStep(step) && step < TOTAL_STEPS - 1) setStep(step + 1); };
  const back = () => { clearTimeout(advanceTimer.current); if (step > 0) setStep(step - 1); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(step)) return;
    setSubmitting(true);
    await sendEmail(true);
    trackFormEvent(FORM_NAME, true);
    if (window.trackValuationRequest) window.trackValuationRequest();
    setSubmitting(false);
    setSubmitted(true);
    setTimeout(() => { window.location.href = '/thank-you'; }, 900);
  };

  const handleSkip = async () => { trackFormEvent(FORM_NAME, false); await sendEmail(false); onClose(); };
  const handleClose = async () => { trackFormEvent(FORM_NAME, false); await sendEmail(false); onClose(); };

  const isLastStep = step === TOTAL_STEPS - 1;
  const autoSteps = [0, 1];
  const showNext = !autoSteps.includes(step) && !isLastStep;
  const showBack = step > 0 && !isLastStep;

  const intros = [
    "Get started by letting us know a little bit about what you need.",
    "Tell us about the condition of your property.",
    "Let's get your estimate! Tell us about your property.",
    "A few more details to make your estimate precise.",
    "One step away from earning more.",
  ];

  return (
    <ModalShell onClose={handleClose}>
      <div className="enrichment-two-col" style={{ display: "grid", gridTemplateColumns: "1.15fr 1fr", gap: 0, background: "#fff", borderRadius: 16, overflow: "hidden", minHeight: 540 }}>
        {/* Form side */}
        <div className="enrichment-form-side" style={{ padding: "44px 40px", display: "flex", flexDirection: "column" }}>
          {/* Brand + progress */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ marginBottom: 16 }}>
              <img src="https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/24682ceeb_TruestaysLogo.png" alt="Truestays" style={{ height: 20, width: "auto" }} />
            </div>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: "#5C7268", margin: "0 0 10px" }}>{intros[step]}</p>
            <div style={{ display: "flex", gap: 6 }}>
              {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= step ? "#112215" : "#E8E4D5", transition: "background 0.3s" }} />
              ))}
            </div>
          </div>

          {/* Step content */}
          <div style={{ flex: 1, minHeight: 0 }} className="enrichment-step-content">
            {step === 0 && (
              <div>
                <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 22, color: "#112215", margin: "0 0 16px", letterSpacing: "-0.01em" }}>What's your main goal? Choose one.</h3>
                {errors.goal && <p style={errStyle}>{errors.goal}</p>}
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {GOALS.map((g) => {
                    const sel = extra.goal === g.id; const Icon = g.icon;
                    return (
                      <button key={g.id} type="button" onClick={() => selectAndAdvance("goal", g.id, 1)}
                        style={{ display: "flex", alignItems: "center", gap: 14, padding: "15px 18px", border: sel ? "2px solid #112215" : "1px solid #E8E4D5", borderRadius: 12, background: sel ? "#F4F1E7" : "#fff", cursor: "pointer", textAlign: "left", transition: "all 0.15s" }}>
                        <div style={{ width: 38, height: 38, borderRadius: 9, background: "#EDE9DC", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <Icon size={18} color="#5C7268" />
                        </div>
                        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, color: "#112215", fontWeight: 500 }}>{g.label}</span>
                        {sel && <CheckCircle2 size={18} color="#112215" style={{ marginLeft: "auto", flexShrink: 0 }} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 1 && (
              <div>
                <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 22, color: "#112215", margin: "0 0 16px", letterSpacing: "-0.01em" }}>What condition is your property in?</h3>
                {errors.condition && <p style={errStyle}>{errors.condition}</p>}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }} className="enrichment-condition-grid">
                  {CONDITIONS.map((c) => {
                    const sel = extra.condition === c.name;
                    return (
                      <button key={c.name} type="button" onClick={() => selectAndAdvance("condition", c.name, 2)}
                        style={{ border: sel ? "2px solid #112215" : "1px solid #E8E4D5", borderRadius: 12, overflow: "hidden", background: "#fff", cursor: "pointer", padding: 0, transition: "all 0.15s", display: "flex", flexDirection: "column" }}>
                        <div style={{ aspectRatio: "4 / 3", overflow: "hidden" }}>
                          <img src={c.img} alt={c.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                        </div>
                        <div style={{ padding: "9px 12px", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 600, color: "#112215" }}>{c.name}</span>
                          {sel && <CheckCircle2 size={15} color="#112215" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 22, color: "#112215", margin: "0 0 16px", letterSpacing: "-0.01em" }}>Tell us about your property</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div>
                    <label style={labelStyle}>Address{isFull ? " *" : ""}</label>
                    <input ref={addressRef} style={{ ...inputStyle, borderColor: errors.address ? "#CD533B" : "#E8E4D5" }} value={extra.address} onChange={setE("address")} placeholder="Start typing your address…" autoComplete="off" />
                    {errors.address && <p style={errStyle}>{errors.address}</p>}
                  </div>
                  <div>
                    <label style={labelStyle}>Property type{isFull ? " *" : ""}</label>
                    <TapOptions value={form.propertyType} onPick={(v) => { setForm(p => ({ ...p, propertyType: v })); if (errors.propertyType) setErrors(prev => ({ ...prev, propertyType: undefined })); }} options={[{ label: "House", value: "House" }, { label: "Apartment", value: "Apartment" }, { label: "Other", value: "Other" }]} columns={3} />
                    {errors.propertyType && <p style={errStyle}>{errors.propertyType}</p>}
                  </div>
                  <div>
                    <label style={labelStyle}>Bedrooms{isFull ? " *" : ""}</label>
                    <TapOptions value={bedNum} onPick={(v) => { setBedNum(v); setForm(p => ({ ...p, bedrooms: numToBed(v) })); if (errors.bedrooms) setErrors(prev => ({ ...prev, bedrooms: undefined })); }} options={BED_OPTS} columns={4} />
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 22, color: "#112215", margin: "0 0 16px", letterSpacing: "-0.01em" }}>A few more details</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div>
                    <label style={labelStyle}>Parking{isFull ? " *" : ""}</label>
                    <TapOptions value={extra.parking} onPick={(v) => { setExtra(p => ({ ...p, parking: v })); if (errors.parking) setErrors(prev => ({ ...prev, parking: undefined })); }} options={PARKING.map(p => ({ label: p, value: p }))} columns={2} />
                    {errors.parking && <p style={errStyle}>{errors.parking}</p>}
                  </div>
                  <div>
                    <label style={labelStyle}>Do you have other properties?{isFull ? " *" : ""}</label>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <button type="button" style={tapBtnStyle(extra.hasOtherProperties === "No")} onMouseEnter={(e) => { if (extra.hasOtherProperties !== "No") e.currentTarget.style.borderColor = "#B8B4A5"; }} onMouseLeave={(e) => { if (extra.hasOtherProperties !== "No") e.currentTarget.style.borderColor = "#E8E4D5"; }} onClick={() => { setExtra(p => ({ ...p, hasOtherProperties: "No" })); if (errors.hasOtherProperties) setErrors(prev => ({ ...prev, hasOtherProperties: undefined })); }}>No</button>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                        <button type="button" style={tapBtnStyle(extra.hasOtherProperties === "Yes (up to 5)")} onMouseEnter={(e) => { if (extra.hasOtherProperties !== "Yes (up to 5)") e.currentTarget.style.borderColor = "#B8B4A5"; }} onMouseLeave={(e) => { if (extra.hasOtherProperties !== "Yes (up to 5)") e.currentTarget.style.borderColor = "#E8E4D5"; }} onClick={() => { setExtra(p => ({ ...p, hasOtherProperties: "Yes (up to 5)" })); if (errors.hasOtherProperties) setErrors(prev => ({ ...prev, hasOtherProperties: undefined })); }}>Yes (up to 5)</button>
                        <button type="button" style={tapBtnStyle(extra.hasOtherProperties === "Yes (more than 5)")} onMouseEnter={(e) => { if (extra.hasOtherProperties !== "Yes (more than 5)") e.currentTarget.style.borderColor = "#B8B4A5"; }} onMouseLeave={(e) => { if (extra.hasOtherProperties !== "Yes (more than 5)") e.currentTarget.style.borderColor = "#E8E4D5"; }} onClick={() => { setExtra(p => ({ ...p, hasOtherProperties: "Yes (more than 5)" })); if (errors.hasOtherProperties) setErrors(prev => ({ ...prev, hasOtherProperties: undefined })); }}>Yes (more than 5)</button>
                      </div>
                    </div>
                    {errors.hasOtherProperties && <p style={errStyle}>{errors.hasOtherProperties}</p>}
                  </div>
                  {extra.goal === "hassle" && (
                    <div>
                      <label style={labelStyle}>Share your listing link <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
                      <input style={inputStyle} value={extra.listingLink} onChange={setE("listingLink")} placeholder="https://www.airbnb.co.uk/rooms/…" />
                    </div>
                  )}
                </div>
              </div>
            )}

            {step === 4 && (
              <form onSubmit={handleSubmit}>
                <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 22, color: "#112215", margin: "0 0 6px", letterSpacing: "-0.01em" }}>Where should we send your estimate?</h3>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: "#5C7268", margin: "0 0 16px", lineHeight: 1.5 }}>
                  We've got what we need to calculate your property's earning potential. Enter your details and we'll be in touch within 24 hours.
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
                  <div>
                    <label style={labelStyle}>Name *</label>
                    <input style={{ ...inputStyle, borderColor: errors.fullName ? "#CD533B" : "#E8E4D5" }} value={form.fullName} onChange={setF("fullName")} placeholder="Your name" />
                    {errors.fullName && <p style={errStyle}>{errors.fullName}</p>}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 11 }} className="enrichment-grid">
                    <div>
                      <label style={labelStyle}>Email *</label>
                      <input style={{ ...inputStyle, borderColor: errors.email ? "#CD533B" : "#E8E4D5" }} type="email" value={form.email} onChange={setF("email")} placeholder="Email address" />
                      {errors.email && <p style={errStyle}>{errors.email}</p>}
                    </div>
                    <div>
                      <label style={labelStyle}>Phone *</label>
                      <input style={{ ...inputStyle, borderColor: errors.phone ? "#CD533B" : "#E8E4D5" }} type="tel" value={form.phone} onChange={setF("phone")} placeholder="07XXX XXXXXX" />
                      {errors.phone && <p style={errStyle}>{errors.phone}</p>}
                    </div>
                  </div>
                  <div style={{ marginTop: 2, display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }} onClick={() => { setReferralConsent(v => !v); if (errors.consent) setErrors(prev => ({ ...prev, consent: undefined })); }}>
                    <div style={{ width: 18, height: 18, borderRadius: 4, flexShrink: 0, marginTop: 1, border: "1.5px solid " + (referralConsent ? "#112215" : (errors.consent ? "#CD533B" : "#D5D0BE")), background: referralConsent ? "#112215" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}>
                      {referralConsent && (<svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>)}
                    </div>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: errors.consent ? "#CD533B" : "#5C7268", lineHeight: 1.45, margin: 0, userSelect: "none" }}>
                      If my property isn't the right fit for Truestays, I'm happy to be referred to one trusted local management company for a valuation instead. No spam, just one relevant introduction.
                    </p>
                  </div>
                  {errors.consent && <p style={errStyle}>{errors.consent}</p>}
                </div>
                <button type="submit" disabled={submitting}
                  style={{ marginTop: 16, background: "#112215", color: "#fff", border: "none", borderRadius: 10, padding: "15px", fontFamily: "'Inter', sans-serif", fontSize: 15, fontWeight: 700, letterSpacing: "0.03em", width: "100%", cursor: "pointer", transition: "background 0.2s", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                  onMouseEnter={e => e.currentTarget.style.background = "#1A3A2A"}
                  onMouseLeave={e => e.currentTarget.style.background = "#112215"}>
                  {submitting ? "Sending…" : <>Request Free Estimates <ArrowRight size={16} /></>}
                </button>
                {submitted && (
                  <div style={{ marginTop: 14, padding: "14px", borderRadius: 10, background: "#EDE9DC", display: "flex", alignItems: "center", gap: 10 }}>
                    <CheckCircle2 size={18} color="#112215" />
                    <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: "#112215", fontWeight: 500 }}>All done! Redirecting…</span>
                  </div>
                )}
              </form>
            )}
          </div>

          {/* Navigation (not on auto-advance steps or last step) */}
          {!isLastStep && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 22 }}>
              <div>
                {showBack && (
                  <button type="button" onClick={back} style={{ background: "none", border: "none", color: "#5C7268", fontFamily: "'Inter', sans-serif", fontSize: 14, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <ArrowLeft size={15} /> Back
                  </button>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                {!isFull && (
                  <button type="button" onClick={handleSkip} style={{ background: "none", border: "none", color: "#5C7268", fontFamily: "'Inter', sans-serif", fontSize: 13, cursor: "pointer", textDecoration: "underline" }}>Skip</button>
                )}
                {showNext && (
                  <button type="button" onClick={next} style={{ background: "#112215", color: "#fff", border: "none", borderRadius: 10, padding: "12px 28px", fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, transition: "background 0.2s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#1A3A2A"}
                    onMouseLeave={e => e.currentTarget.style.background = "#112215"}>
                    Next <ArrowRight size={15} />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Info side */}
        <EnrichmentInfoPanel step={step} />
      </div>
    </ModalShell>
  );
}

function ModalShell({ children, onClose }) {
  useEffect(() => {
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    };
  }, []);

  return createPortal(
    <div className="enrichment-modal-overlay"
      style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}
      onClick={e => { if (e.target === e.currentTarget && onClose) onClose(); }}>
      <div className="enrichment-modal-shell" style={{ width: "100%", maxWidth: 920, maxHeight: "92vh", boxShadow: "0 24px 80px rgba(0,0,0,0.3)", position: "relative", display: "flex", flexDirection: "column" }}>
        <style>{`
          @media (max-width: 768px) {
            .enrichment-modal-overlay { padding: 0 !important; align-items: stretch !important; background: rgba(0,0,0,0.4) !important; backdrop-filter: none !important; -webkit-backdrop-filter: none !important; }
            .enrichment-modal-shell { max-width: 100% !important; width: 100% !important; height: 100dvh !important; max-height: 100dvh !important; border-radius: 0 !important; }
            .enrichment-two-col { grid-template-columns: 1fr !important; height: 100dvh !important; border-radius: 0 !important; }
            .enrichment-info-side { display: none !important; }
            .enrichment-form-side { padding: 44px 18px 18px 18px !important; height: 100dvh !important; overflow: hidden !important; display: flex !important; flex-direction: column !important; box-sizing: border-box !important; }
            .enrichment-step-content { overflow-y: auto !important; -webkit-overflow-scrolling: touch; }
          }
          @media (max-width: 600px) {
            .enrichment-grid { grid-template-columns: 1fr !important; }
            .enrichment-condition-grid { grid-template-columns: 1fr 1fr !important; gap: 8px !important; }
          }
          @media (max-width: 480px) {
            .enrichment-form-side { padding: 40px 16px 16px 16px !important; }
            .enrichment-modal-shell { width: 100vw !important; max-width: 100vw !important; }
            .enrichment-form-side input, .enrichment-form-side select { width: 100% !important; box-sizing: border-box !important; }
          }
        `}</style>
        {onClose && (
          <button onClick={onClose} className="enrichment-modal-close"
            style={{ position: "absolute", top: 16, right: 16, zIndex: 10, background: "#EDE9DC", border: "none", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X size={16} color="#5C7268" />
          </button>
        )}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}